import type { TtsRequestOptions } from "./api";
import { getCachedTtsBase64, prefetchTtsBase64 } from "./ttsCache";
import { buildSlideTtsScript } from "./script";
import type { SlideData } from "@/types/lesson";

const TTS_TIMEOUT_MS = 20000;

function base64ToBlob(base64: string, mime: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Incremented when playback should stop (slide change, user stop, new speak). */
let playbackGeneration = 0;
let currentAudio: HTMLAudioElement | null = null;
let currentBlobUrl: string | null = null;

export function cancelTtsPlayback(): void {
  playbackGeneration++;
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = "";
    currentAudio = null;
  }
  if (currentBlobUrl) {
    URL.revokeObjectURL(currentBlobUrl);
    currentBlobUrl = null;
  }
}

export interface TtsContext {
  isChild?: boolean;
  audienceLabel?: string;
  yearLabel?: string;
}

export function ttsOptionsFromContext(context?: TtsContext): TtsRequestOptions {
  return {
    voice: context?.isChild ? "fable" : "nova",
    accent: "british",
    style: "tutor",
    audience: context?.audienceLabel,
    year_group: context?.yearLabel,
    instructions:
      "Use a natural, warm British English accent. Speak clearly like a friendly UK tutor for GCSE and EAL learners.",
  };
}

function playBase64Mp3(
  base64: string,
  gen: number,
  textLen?: number,
  onProgress?: (ci: number) => void
): Promise<void> {
  const blob = base64ToBlob(base64, "audio/mpeg");
  const url = URL.createObjectURL(blob);
  currentBlobUrl = url;
  const audio = new Audio(url);
  currentAudio = audio;

  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      if (currentBlobUrl === url) {
        URL.revokeObjectURL(url);
        currentBlobUrl = null;
      }
      if (currentAudio === audio) currentAudio = null;
    };

    const finish = () => {
      cleanup();
      resolve();
    };

    // Emit estimated char position using audio time ratio.
    if (onProgress && textLen) {
      audio.ontimeupdate = () => {
        if (gen !== playbackGeneration) return;
        if (!isFinite(audio.duration) || audio.duration === 0) return;
        const ci = Math.round((audio.currentTime / audio.duration) * textLen);
        onProgress(Math.min(ci, textLen - 1));
      };
    }

    audio.onended = () => {
      if (gen !== playbackGeneration) return;
      finish();
    };

    audio.onerror = () => {
      cleanup();
      reject(audio.error ?? new Error("Audio playback failed"));
    };

    audio.onpause = () => {
      if (settled) return;
      if (audio.ended && gen === playbackGeneration) finish();
      else if (!audio.ended && gen !== playbackGeneration) finish();
    };

    audio.play().catch((err) => {
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err)));
    });
  });
}

/** When server TTS is unavailable (e.g. not logged in), use the browser voice. */
function speakBrowserSynth(
  text: string,
  gen: number,
  onProgress?: (ci: number) => void
): Promise<void> {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return Promise.reject(new Error("No speech synthesis"));
  }
  return new Promise((resolve, reject) => {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-GB";
    u.rate = 0.95;
    if (onProgress) {
      u.onboundary = (e) => {
        if (gen !== playbackGeneration) return;
        if (e.name === "word") onProgress(e.charIndex);
      };
    }
    u.onend = () => {
      if (gen !== playbackGeneration) return;
      resolve();
    };
    u.onerror = (e) => {
      if (gen !== playbackGeneration) return;
      reject(new Error(e.error || "speech synthesis failed"));
    };
    window.speechSynthesis.speak(u);
  });
}

export async function speakTts(
  text: string,
  context?: TtsContext,
  onProgress?: (charIndex: number) => void
): Promise<void> {
  if (!text.trim()) return;

  const gen = playbackGeneration;
  const textLen = text.trim().length;

  const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T> =>
    Promise.race([
      p,
      new Promise<T>((_, rej) => setTimeout(() => rej(new Error("timeout")), ms)),
    ]);

  try {
    const opts = ttsOptionsFromContext(context);
    const base64 = await withTimeout(getCachedTtsBase64(text.trim(), opts), TTS_TIMEOUT_MS);
    if (gen !== playbackGeneration) return;
    await playBase64Mp3(base64, gen, textLen, onProgress);
    if (gen !== playbackGeneration) return;
  } catch (error) {
    if (gen !== playbackGeneration) return;
    try {
      await speakBrowserSynth(text.trim(), gen, onProgress);
      if (gen !== playbackGeneration) return;
    } catch {
      throw new Error(
        error instanceof Error ? `Voice playback failed: ${error.message}` : "Voice playback failed"
      );
    }
  }
}

/**
 * Prefetch current slide + next slides so narration is ready with minimal delay when advancing.
 */
export function prefetchLessonTtsSlides(
  slides: SlideData[],
  currentIndex: number,
  context?: TtsContext
): void {
  const opts = ttsOptionsFromContext(context);
  const indices = [currentIndex, currentIndex + 1, currentIndex + 2, currentIndex + 3, currentIndex + 4].filter(
    (i) => i >= 0 && i < slides.length
  );

  const schedule = () => {
    for (const i of indices) {
      const script = buildSlideTtsScript(slides[i]);
      prefetchTtsBase64(script, opts);
    }
  };

  // Run ASAP so the first slide’s audio starts loading immediately (no idle delay).
  if (typeof queueMicrotask !== "undefined") {
    queueMicrotask(schedule);
  } else {
    setTimeout(schedule, 0);
  }
}
